import React, { useState } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { MapPin, Package, AlertCircle, AlertTriangle, TriangleAlert, Edit3, Pencil, X, Image, Layers, Truck } from 'lucide-react';
import { PickingLine } from './types';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { getStructureCapNote, hasStructureDeficit } from './structureCaps';

interface ActiveLineProps {
  activeLine: PickingLine | null;
  onScanRequired: () => void;
  onLocationRequired: () => void;
  editingLocation?: number | null;
  editingQuantity?: number | null;
  newLocation?: string;
  newQuantity?: string;
  onStartLocationEdit?: (lineNo: number, location: string) => void;
  onStartQuantityEdit?: (lineNo: number, quantity: string) => void;
  onLocationChange?: (value: string) => void;
  onQuantityChange?: (value: string) => void;
  onLocationEdit?: (lineNo: number, location: string) => void;
  onQuantityEdit?: (lineNo: number, quantity: number) => void;
  // Åpner antall-modalen (setTotal) — brukes av blyant-knappen i mobilgrenen.
  onOpenQuantityEditModal?: (lineNo: number) => void;
  onShowImage?: () => void;
  // Saldo for gjeldende plukklokasjon (valgfritt — rendres bare når satt).
  qtyOnLocation?: number | null;
  reservedOnLocation?: number | null;
  // Åpner lokasjonsoversikt-modalen (valgfritt — ikon vises bare når satt).
  onShowLocationStock?: () => void;
  // Leveringsmåte(r) for ordren(e) på plukket — vises i headeren.
  deliveryMethodText?: string | null;
  // True når plukket er delt på flere plukklister (brødre) og MÅ sendes til pakking.
  mustGoToPacking?: boolean;
  // Strukturplukk: åpner telle-snarveien for aktiv linje (vises kun i maks mulig sett-varselet).
  onStructureCount?: () => void;
  /** picking.largeText (TW 36393697): større skrift og trykkmål for nettbrett. */
  largeText?: boolean;
}

export const ActiveLine: React.FC<ActiveLineProps> = ({
  activeLine,
  onScanRequired,
  onLocationRequired,
  editingLocation,
  editingQuantity,
  newLocation = '',
  newQuantity = '',
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onLocationEdit,
  onQuantityEdit,
  onOpenQuantityEditModal,
  onShowImage,
  qtyOnLocation,
  reservedOnLocation,
  onShowLocationStock,
  deliveryMethodText,
  mustGoToPacking,
  onStructureCount,
  largeText = false
}) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullItemNo, setShowFullItemNo] = useState(false);
  const [showFullOrderCustomer, setShowFullOrderCustomer] = useState(false);

  // Varebilde (36334101): lightbox + stille 404-håndtering. Feil-flagget nullstilles
  // når aktiv linje (og dermed bilde-URL) skifter.
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  React.useEffect(() => {
    setImageFailed(false);
    setShowImageLightbox(false);
  }, [activeLine?.image]);

  const handleKeyPress = (e: React.KeyboardEvent, type: 'location' | 'quantity') => {
    if (e.key === 'Enter' && activeLine) {
      if (type === 'location' && onLocationEdit) {
        onLocationEdit(activeLine.pListLineNo, newLocation);
      } else if (type === 'quantity' && onQuantityEdit) {
        onQuantityEdit(activeLine.pListLineNo, parseFloat(newQuantity) || 0);
      }
    }
  };

  if (!activeLine) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-sm p-2 sm:p-4 mb-2 sm:mb-4">
        <div className="text-center text-gray-500">
          <Package size={20} className="mx-auto mb-1 text-gray-300 sm:w-6 sm:h-6 sm:mb-2" />
          <p className="text-xs sm:text-sm font-medium">{t('warehouse.modules.picking.labels.noActiveLineShort')}</p>
        </div>
      </div>
    );
  }

  const isDescriptionTruncated = activeLine.itemDescription && activeLine.itemDescription.length > 40;
  const isItemNoTruncated = activeLine.itemNo && activeLine.itemNo.length > 20;

  // Strukturvarer: «maks mulig sett»-advarsel for aktiv komponentlinje.
  // Telle-snarveien tilbys kun når linjen selv har saldoavvik (ikke når den bare er
  // begrenset av manko på en annen komponent).
  const structureCapNote = getStructureCapNote(activeLine, t);
  const showStructureCount = onStructureCount !== undefined && hasStructureDeficit(activeLine);

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg shadow-sm p-2 sm:p-5 mb-2 sm:mb-4">
      {/* MOBILE: Stacked layout with tap-to-expand */}
      <div className="sm:hidden">
        {/* Row 0: Ordrenr + kunde — alltid synlig, kompakt, trykk for full tekst (Ariens) */}
        {(activeLine.orderNoERP || activeLine.customerName) && (
          <button
            onClick={() => setShowFullOrderCustomer(!showFullOrderCustomer)}
            className={`flex items-center gap-1.5 w-full text-left ${largeText ? 'text-sm flex-wrap' : 'text-xs'} mb-1 min-w-0 hover:text-blue-700 transition-colors`}
          >
            {activeLine.orderNoERP && (
              <span className="font-semibold text-gray-800 flex-shrink-0">#{activeLine.orderNoERP}</span>
            )}
            {activeLine.orderNoERP && activeLine.customerName && (
              <span className="text-gray-400 flex-shrink-0">·</span>
            )}
            {activeLine.customerName && (
              <span className={`text-gray-600 min-w-0 ${showFullOrderCustomer || largeText ? 'break-words' : 'truncate'}`}>
                {activeLine.customerName}
              </span>
            )}
          </button>
        )}
        {/* Leveringsmåte + til-pakking-indikator (TW 36320100/36320101) */}
        {(deliveryMethodText || mustGoToPacking) && (
          <div className="flex items-center gap-1.5 text-xs mb-1 min-w-0">
            {deliveryMethodText && (
              <>
                <Truck size={12} className="text-blue-600 flex-shrink-0" />
                <span className="text-gray-600 truncate">{deliveryMethodText}</span>
              </>
            )}
            {mustGoToPacking && (
              <span className="font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                {t('warehouse.modules.picking.labels.goesToPacking', {}, 'Skal til pakking')}
              </span>
            )}
          </div>
        )}
        {/* Row 1: Item number (full line, tap to expand) */}
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse flex-shrink-0"></div>
          <button
            onClick={() => setShowFullItemNo(!showFullItemNo)}
            className={`font-bold ${largeText ? 'text-xl' : 'text-sm'} text-gray-900 text-left flex-1 min-w-0 hover:text-blue-700 transition-colors`}
          >
            {showFullItemNo ? (
              <span>{activeLine.itemNo}</span>
            ) : (
              <span className="truncate block">{activeLine.itemNo}</span>
            )}
          </button>
          {activeLine.image && onShowImage && (
            <button
              onClick={onShowImage}
              className={`p-1.5 text-blue-600 hover:bg-blue-100 rounded ${largeText ? 'min-w-[52px] min-h-[52px]' : 'min-w-[36px] min-h-[36px]'} flex items-center justify-center flex-shrink-0`}
            >
              <Image size={largeText ? 22 : 16} />
            </button>
          )}
        </div>

        {/* Row 2: Description (full line, tap to expand) */}
        <div className="mb-1 pl-4">
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className={`${largeText ? 'text-base text-gray-700' : 'text-xs text-gray-600'} text-left w-full hover:text-blue-700 transition-colors`}
          >
            {showFullDescription || largeText ? (
              <span>{activeLine.itemDescription}</span>
            ) : (
              <span className="truncate block">{activeLine.itemDescription}</span>
            )}
          </button>
        </div>

        {/* Varebilde inline på nettbrett (TW 36393698): fra 560 px («tablet») også i mobilblokken —
            Galaxy Tab i portrett (600 CSS-px) fikk bare ikonet. Håndterminaler (< 560 px) urørt.
            Brukket bilde skjules helt (samme 404-håndtering som desktop). Trykk → full størrelse. */}
        {activeLine.image && !imageFailed && (
          <div className="hidden tablet:block pl-4 mb-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowImageLightbox(true); }}
              className="rounded-lg border border-gray-200 overflow-hidden bg-white active:border-blue-400 min-h-[44px]"
              aria-label={t('warehouse.modules.picking.itemImage.showFull', {}, 'Vis varebilde i full størrelse')}
            >
              <img
                src={activeLine.image}
                alt={activeLine.itemNo || ''}
                loading="lazy"
                className={`${largeText ? 'h-32 w-32' : 'h-24 w-24'} object-contain bg-white`}
                onError={() => setImageFailed(true)}
              />
            </button>
          </div>
        )}

        {/* Row 3: Location + Quantity (ordrenr flyttet til Row 0) — redigerbar med synlige blyant-knapper */}
        <div className={`flex items-center gap-1 ${largeText ? 'text-xl flex-wrap' : 'text-sm'} pl-4 mb-1 min-w-0`}>
          {editingLocation === activeLine.pListLineNo ? (
            <ScanTextInput
              value={newLocation}
              onChange={(e) => onLocationChange?.(e.target.value)}
              onBlur={() => onLocationEdit?.(activeLine.pListLineNo, newLocation)}
              onKeyPress={(e) => handleKeyPress(e, 'location')}
              className="font-medium flex-1 min-w-0 px-2 py-2 border-2 border-blue-400 rounded min-h-[44px]"
              autoFocus
            />
          ) : (
            <>
              <span className="font-medium text-gray-700 truncate min-w-0">{activeLine.location}</span>
              <button
                type="button"
                onClick={() => onStartLocationEdit?.(activeLine.pListLineNo, activeLine.binCode || activeLine.location)}
                className={`p-1.5 rounded hover:bg-blue-100 text-gray-500 ${largeText ? 'min-h-[52px] min-w-[52px]' : 'min-h-[44px] min-w-[44px]'} flex items-center justify-center flex-shrink-0`}
                aria-label={t('warehouse.modules.picking.labels.editLocation', {}, 'Endre lokasjon')}
              >
                <Pencil size={largeText ? 22 : 16} />
              </button>
              {onShowLocationStock && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onShowLocationStock(); }}
                  className={`p-1.5 rounded hover:bg-blue-100 text-blue-600 ${largeText ? 'min-h-[52px] min-w-[52px]' : 'min-h-[44px] min-w-[44px]'} flex items-center justify-center flex-shrink-0`}
                  aria-label={t('warehouse.modules.picking.locationStock.openTitle', {}, 'Vis lokasjoner / saldo')}
                >
                  <Layers size={largeText ? 24 : 18} />
                </button>
              )}
              <span className="text-gray-400 flex-shrink-0">•</span>
              <span className="font-bold text-blue-700 flex-shrink-0">{activeLine.qtyPicked}/{activeLine.qty}{activeLine.unitTxt ? ` ${activeLine.unitTxt}` : ''}</span>
              {activeLine.itemType === 6 ? (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex-shrink-0">SN</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenQuantityEditModal?.(activeLine.pListLineNo)}
                  className={`p-1.5 rounded hover:bg-blue-100 text-gray-500 ${largeText ? 'min-h-[52px] min-w-[52px]' : 'min-h-[44px] min-w-[44px]'} flex items-center justify-center flex-shrink-0`}
                  aria-label={t('warehouse.modules.picking.labels.editQuantity', {}, 'Endre antall')}
                >
                  <Pencil size={largeText ? 22 : 16} />
                </button>
              )}
            </>
          )}
        </div>

        {/* Farlig gods (TW 36383256): varsel før plukkeren tar varen. Compass eier ikke
            klassifiseringen (UN-nummer, klasse, emballasjegruppe) — den ligger hos
            transportøren — så her står bare selve advarselen. */}
        {activeLine.isDangerousGoods && (
          <div className="flex items-center gap-1.5 bg-red-50 border border-red-300 rounded px-2 py-1.5 mb-1 ml-4">
            <TriangleAlert size={14} className="text-red-600 flex-shrink-0" />
            <span className="text-xs font-bold text-red-800">
              {t('warehouse.modules.picking.labels.dangerousGoodsWarning', {}, 'Farlig gods — følg gjeldende rutiner for ADR-håndtering')}
            </span>
          </div>
        )}

        {/* Strukturvare: maks mulig sett-advarsel + telle-snarvei */}
        {structureCapNote && !activeLine.isFullyPicked && (
          <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-300 rounded px-2 py-1.5 mb-1 ml-4">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-800 flex-1 min-w-0">{structureCapNote}</span>
            {showStructureCount && (
              <button
                type="button"
                onClick={onStructureCount}
                className="text-xs font-bold text-amber-900 bg-amber-200 hover:bg-amber-300 px-2 py-1.5 rounded flex-shrink-0 min-h-[32px]"
              >
                {t('warehouse.modules.picking.structure.countLocation', {}, 'Tell lokasjon')}
              </button>
            )}
          </div>
        )}

        {/* Row 4: Extra info fields from OrderLine (configured via UserProfile) */}
        {(activeLine.extraInfoField2Value || activeLine.extraInfoField3Value) && (
          <div className="flex items-center gap-2 text-xs font-medium pl-4">
            {activeLine.extraInfoField2Value && (
              <span className="text-red-600">{activeLine.extraInfoField2Value}</span>
            )}
            {activeLine.extraInfoField3Value && (
              <span className="text-green-600">{activeLine.extraInfoField3Value}</span>
            )}
          </div>
        )}
      </div>

      {/* DESKTOP: Full layout */}
      <div className="hidden sm:block">
        {/* Header with Order Info */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className={largeText ? 'flex flex-col items-start gap-1 min-w-0' : 'flex items-center gap-4'}>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"></div>
              <h3 className="text-base font-semibold text-blue-900">{t('warehouse.modules.picking.sections.activePickingLine')}</h3>
            </div>
            <div className={largeText ? 'flex flex-col items-start gap-0.5 text-base min-w-0' : 'flex items-center gap-4 text-sm'}>
              {activeLine.orderNoERP && (
                <div className="text-gray-700 font-medium">{activeLine.orderNoERP}</div>
              )}
              {activeLine.customerName && (
                <div className={`text-gray-600 ${largeText ? 'text-lg font-medium break-words' : ''}`}>{activeLine.customerName}</div>
              )}
              {/* Leveringsmåte + til-pakking-indikator (TW 36320100/36320101) */}
              {deliveryMethodText && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Truck size={14} className="text-blue-600 flex-shrink-0" />
                  <span>{deliveryMethodText}</span>
                </div>
              )}
              {mustGoToPacking && (
                <span className="text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {t('warehouse.modules.picking.labels.goesToPacking', {}, 'Skal til pakking')}
                </span>
              )}
            </div>
          </div>
          <div className="text-sm font-medium text-blue-700">#{activeLine.pListLineNo}</div>
        </div>

        {/* Farlig gods (TW 36383256) */}
        {activeLine.isDangerousGoods && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-md px-3 py-2 mb-3">
            <TriangleAlert size={18} className="text-red-600 flex-shrink-0" />
            <span className="text-sm font-bold text-red-800">
              {t('warehouse.modules.picking.labels.dangerousGoodsWarning', {}, 'Farlig gods — følg gjeldende rutiner for ADR-håndtering')}
            </span>
          </div>
        )}

        {/* Strukturvare: maks mulig sett-advarsel + telle-snarvei */}
        {structureCapNote && !activeLine.isFullyPicked && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 mb-3">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-amber-800 flex-1">{structureCapNote}</span>
            {showStructureCount && (
              <button
                type="button"
                onClick={onStructureCount}
                className="text-sm font-bold text-amber-900 bg-amber-200 hover:bg-amber-300 px-3 py-1.5 rounded flex-shrink-0"
              >
                {t('warehouse.modules.picking.structure.countLocation', {}, 'Tell lokasjon')}
              </button>
            )}
          </div>
        )}

        {/* Content with larger elements */}
        <div className="bg-white rounded-md p-5 border border-blue-200">
          <div className="grid grid-cols-3 gap-6">
            {/* Item Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Package size={18} className="text-blue-600" />
                <span className={`${largeText ? 'text-base' : 'text-sm'} font-medium text-gray-600`}>{t('warehouse.modules.picking.labels.item')}</span>
                {activeLine.image && onShowImage && (
                  <button
                    onClick={onShowImage}
                    className="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-md transition-colors border border-blue-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('warehouse.modules.picking.labels.showHideImage')}
                  >
                    <Image size={20} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold text-gray-900 mb-1`}>{activeLine.itemNo}</div>
              <div className="relative">
                {!showFullDescription && !largeText ? (
                  <div className="flex items-center gap-1 overflow-hidden">
                    <div className="text-sm text-gray-600 truncate flex-1">{activeLine.itemDescription}</div>
                    {isDescriptionTruncated && (
                      <button
                        onClick={() => setShowFullDescription(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex-shrink-0 min-h-[44px] px-2"
                      >
                        ...
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={largeText ? 'text-lg text-gray-700 break-words' : 'text-sm text-gray-600'}>
                    {activeLine.itemDescription}
                    <button
                      onClick={() => setShowFullDescription(false)}
                      className="ml-2 text-blue-600 hover:text-blue-700 inline-flex items-center min-h-[44px] px-2"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
              {/* Extra info fields from OrderLine (configured via UserProfile) */}
              {(activeLine.extraInfoField2Value || activeLine.extraInfoField3Value) && (
                <div className="flex items-center gap-3 mt-1">
                  {activeLine.extraInfoField2Value && (
                    <span className="text-sm font-semibold text-red-600">{activeLine.extraInfoField2Value}</span>
                  )}
                  {activeLine.extraInfoField3Value && (
                    <span className="text-sm font-semibold text-green-600">{activeLine.extraInfoField3Value}</span>
                  )}
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={18} className="text-blue-600" />
                <span className={`${largeText ? 'text-base' : 'text-sm'} font-medium text-gray-600`}>{t('warehouse.modules.picking.labels.location')}</span>
              </div>
              {editingLocation === activeLine.pListLineNo ? (
                <ScanTextInput
                  value={newLocation}
                  onChange={(e) => onLocationChange?.(e.target.value)}
                  onBlur={() => onLocationEdit?.(activeLine.pListLineNo, newLocation)}
                  onKeyPress={(e) => handleKeyPress(e, 'location')}
                  className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold w-full px-2 py-2 border-2 border-blue-400 rounded min-h-[44px]`}
                  autoFocus
                />
              ) : (
                <div>
                  <div className="flex items-center gap-1">
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 rounded px-2 py-2 -ml-2 min-h-[44px]"
                      onClick={() => onStartLocationEdit?.(activeLine.pListLineNo, activeLine.binCode || activeLine.location)}
                    >
                      <div className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold text-gray-900`}>{activeLine.location}</div>
                      <Edit3 size={largeText ? 22 : 16} className="text-gray-400" />
                    </div>
                    {onShowLocationStock && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onShowLocationStock(); }}
                        className="p-2 rounded hover:bg-blue-50 text-blue-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                        title={t('warehouse.modules.picking.locationStock.openTitle', {}, 'Vis lokasjoner / saldo')}
                        aria-label={t('warehouse.modules.picking.locationStock.openTitle', {}, 'Vis lokasjoner / saldo')}
                      >
                        <Layers size={18} />
                      </button>
                    )}
                  </div>
                  {(qtyOnLocation != null || reservedOnLocation != null) && (
                    <div className="text-xs text-gray-500 px-2 -ml-2 mt-0.5">
                      {t('warehouse.modules.picking.locationStock.onHand', {}, 'På lager')}: <span className="font-medium text-gray-700">{qtyOnLocation ?? '-'}</span>
                      {' · '}
                      {t('warehouse.modules.picking.locationStock.reserved', {}, 'Reservert')}: <span className="font-medium text-amber-700">{reservedOnLocation ?? 0}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quantity */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={18} className="text-blue-600" />
                <span className={`${largeText ? 'text-base' : 'text-sm'} font-medium text-gray-600`}>{t('warehouse.modules.picking.labels.quantity')}</span>
              </div>
              {activeLine.itemType === 6 ? (
                /* ItemType=6: Serial-tracked - quantity only changes via serial pick modal */
                <div className="flex items-center gap-2 px-2 py-2 -ml-2 min-h-[44px]">
                  <div className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold text-gray-900`}>
                    {activeLine.qtyPicked} av {activeLine.qty}{activeLine.unitTxt ? ` ${activeLine.unitTxt}` : ''}
                  </div>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">SN</span>
                </div>
              ) : editingQuantity === activeLine.pListLineNo ? (
                <NumericInput
                  mode="decimal"
                  value={newQuantity}
                  onChange={(e) => onQuantityChange?.(e.target.value)}
                  onBlur={() => onQuantityEdit?.(activeLine.pListLineNo, parseFloat(newQuantity) || 0)}
                  onKeyPress={(e) => handleKeyPress(e, 'quantity')}
                  className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold w-full px-2 py-2 border-2 border-blue-400 rounded min-h-[44px]`}
                  autoFocus
                />
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 rounded px-2 py-2 -ml-2 min-h-[44px]"
                  onClick={() => onStartQuantityEdit?.(activeLine.pListLineNo, activeLine.qtyPicked.toString())}
                >
                  <div className={`${largeText ? 'text-3xl' : 'text-xl'} font-bold text-gray-900`}>
                    {activeLine.qtyPicked} av {activeLine.qty}{activeLine.unitTxt ? ` ${activeLine.unitTxt}` : ''}
                  </div>
                  <Edit3 size={largeText ? 22 : 16} className="text-gray-400" />
                </div>
              )}

              {/* Varebilde (36334101): til høyre under Plukket antall. Vises kun når
                  items.imageBaseUrl er satt og linjen har bildenavn (resolves i usePickingData).
                  Stille 404-håndtering: brukket bilde skjules helt. Trykk → full størrelse. */}
              {activeLine.image && !imageFailed && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowImageLightbox(true); }}
                    className="rounded-lg border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors min-h-[44px]"
                    aria-label={t('warehouse.modules.picking.itemImage.showFull', {}, 'Vis varebilde i full størrelse')}
                  >
                    <img
                      src={activeLine.image}
                      alt={activeLine.itemNo || ''}
                      loading="lazy"
                      className="h-40 w-40 object-contain bg-white"
                      onError={() => setImageFailed(true)}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox for varebilde (36334101) */}
      {showImageLightbox && activeLine.image && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImageLightbox(false)}
        >
          {/* w/h-eksplisitt (ikke bare max-*) så små kildebilder skaleres OPP til ~80 % av skjermen */}
          <div className="relative w-[80vw] h-[80vh] flex flex-col">
            <button
              onClick={() => setShowImageLightbox(false)}
              className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-2 shadow-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('common.buttons.close', {}, 'Lukk')}
            >
              <X size={20} />
            </button>
            <img
              src={activeLine.image}
              alt={activeLine.itemNo || ''}
              className="flex-1 min-h-0 w-full object-contain rounded-lg bg-white"
            />
            <div className="text-center text-white text-sm mt-2">
              {activeLine.itemNo} — {activeLine.itemDescription}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
